/**
 * Socle des sélecteurs en modale (exercice, SKILLCARD…) — remplace les listes
 * déroulantes quand on choisit UN élément parmi beaucoup.
 *
 * - `PickerField` : le « champ » (bouton) qui affiche le choix courant et ouvre
 *   la modale. Zone de tap ≥ 44 px, pleine largeur.
 * - `PickerModal` : plein écran sur mobile, fenêtre centrée dès `sm`. Recherche
 *   en haut (focus automatique), filtres en puces, liste qui défile DANS la
 *   modale (la page derrière est bloquée). Échap / croix / tap à côté ferment.
 *   Rendue dans un portail (jamais enfermée dans un bloc animé).
 */

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, Search, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';

export function PickerField({
  id,
  label,
  placeholder,
  selected,
  onOpen,
  disabled = false,
}: {
  id?: string;
  label?: string;
  placeholder: string;
  /** Rendu du choix courant ; null = rien de choisi (placeholder). */
  selected: ReactNode | null;
  onOpen: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      {label && (
        <label htmlFor={id} className="text-ink-700 block text-sm font-semibold">
          {label}
        </label>
      )}
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        onClick={onOpen}
        className="border-ink-200 hover:border-primary-300 flex min-h-[2.75rem] w-full items-center gap-3 rounded-xl border bg-white px-3 py-2 text-left text-sm transition active:scale-[0.99] disabled:opacity-50"
      >
        <span className="min-w-0 flex-1">
          {selected ?? <span className="text-ink-400">{placeholder}</span>}
        </span>
        <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" aria-hidden />
      </button>
    </div>
  );
}

export interface PickerChip {
  value: string;
  label: string;
  count?: number;
}

export function PickerModal({
  title,
  query,
  onQuery,
  searchPlaceholder,
  chips,
  chipValue,
  onChip,
  allChipsLabel = 'Tous',
  onClose,
  footer,
  children,
}: {
  title: string;
  query: string;
  onQuery: (q: string) => void;
  searchPlaceholder: string;
  /** Filtre en puces (ex. niveaux) ; absent ou < 2 valeurs = pas de puces. */
  chips?: PickerChip[];
  chipValue?: string;
  onChip?: (value: string) => void;
  allChipsLabel?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[70] flex items-stretch justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="ocean:bg-[#061433] native:pt-[env(safe-area-inset-top)] flex h-full w-full flex-col bg-white shadow-2xl sm:h-auto sm:max-h-[85dvh] sm:max-w-lg sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-ink-100 ocean:border-white/10 space-y-3 border-b px-4 pb-3 pt-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-ink-900 text-lg font-bold">{title}</h2>
            <button
              type="button"
              aria-label="Fermer"
              onClick={onClose}
              className="text-ink-500 -mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
          <label className="relative block">
            <span className="sr-only">{searchPlaceholder}</span>
            <Search
              className="text-ink-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
              aria-hidden
            />
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="border-ink-200 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border bg-white pl-9 pr-3 text-sm focus:outline-none"
            />
          </label>
          {chips && chips.length > 1 && onChip && (
            <div
              className="-mx-4 flex gap-2 overflow-x-auto px-4"
              role="group"
              aria-label="Filtrer"
            >
              {[{ value: '', label: allChipsLabel }, ...chips].map((c) => {
                const on = (chipValue ?? '') === c.value;
                return (
                  <button
                    key={c.value || 'all'}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onChip(c.value)}
                    className={cn(
                      'min-h-[2.75rem] shrink-0 rounded-full border px-4 text-sm font-semibold',
                      on
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-ink-200 text-ink-700 bg-white',
                    )}
                  >
                    {c.label}
                    {c.count != null && <span className="ml-1 opacity-70">{c.count}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {children}
        </div>
        {footer && (
          <div className="border-ink-100 ocean:border-white/10 border-t px-4 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Ligne de liste d'un sélecteur (choix au tap, coché si sélectionnée). */
export function PickerOption({
  selected,
  onSelect,
  children,
  label,
}: {
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
  /** Nom accessible (texte brut). */
  label: string;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      aria-label={label}
      onClick={onSelect}
      className={cn(
        'flex min-h-[2.75rem] w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition active:scale-[0.99]',
        selected ? 'bg-primary-50 ring-primary-300 ring-1' : 'hover:bg-ink-50',
      )}
    >
      {children}
    </button>
  );
}
